import { api, throwApiError } from "../../../api/api";

/** Filtros da tela "Consumo de IA" (dias de São Paulo, `YYYY-MM-DD`). */
export type AiUsageFilters = {
  from: string;
  to: string;
  /** Id do usuário ou `SISTEMA`. */
  userId?: string;
  feature?: string;
  /** Quem cobrou: Anthropic (textos) ou Google (imagens). */
  provider?: AiProvider;
  success?: "true" | "false";
};

export type AiProvider = "ANTHROPIC" | "GOOGLE";

export type AiUsageRow = {
  id: string;
  occurredAt: string;
  userId: string | null;
  userName: string;
  feature: string;
  featureLabel: string;
  provider: AiProvider;
  model: string;
  /** "Google · gemini-3-pro-image" */
  modelLabel: string;
  imageCount: number | null;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costUsd: number;
  durationMs: number;
  success: boolean;
  errorMessage: string | null;
  entityType: string | null;
  entityId: string | null;
  summary: string | null;
};

export type AiUsageSummary = {
  period: { from: string; to: string };
  totals: { calls: number; failures: number; inputTokens: number; outputTokens: number; images: number; costUsd: number };
  byDay: { date: string; calls: number; costUsd: number }[];
  byUser: { userId: string; name: string; calls: number; costUsd: number }[];
  byFeature: { feature: string; label: string; calls: number; costUsd: number }[];
  byModel: { provider: AiProvider; model: string; label: string; calls: number; costUsd: number }[];
  month: { spentUsd: number; limitUsd: number; reached: boolean };
  dollarRate: number | null;
  enabled: boolean;
  model: string;
  imageEnabled: boolean;
  imageModel: string;
  options: {
    providers: { id: AiProvider; label: string }[];
    users: { id: string; name: string }[]; features: { feature: string; label: string }[] };
};

const params = (f: AiUsageFilters) => Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== ""));

export const getAiUsageSummaryApi = async (filters: AiUsageFilters): Promise<AiUsageSummary> => {
  try {
    const { data } = await api.get<AiUsageSummary>("/ai-usage/summary", { params: params(filters) });
    return data;
  } catch (error) {
    return throwApiError(error, "Erro ao carregar o consumo de IA.");
  }
};

export const listAiUsageApi = async (
  filters: AiUsageFilters,
  page: number,
  pageSize = 25,
): Promise<{ total: number; page: number; pageSize: number; items: AiUsageRow[] }> => {
  try {
    const { data } = await api.get("/ai-usage", { params: { ...params(filters), page, pageSize } });
    return data;
  } catch (error) {
    return throwApiError(error, "Erro ao listar as chamadas de IA.");
  }
};

export const exportAiUsageApi = async (filters: AiUsageFilters): Promise<Blob> => {
  try {
    const { data } = await api.get("/ai-usage/export", { params: params(filters), responseType: "blob" });
    return data;
  } catch (error) {
    return throwApiError(error, "Erro ao exportar o consumo de IA.");
  }
};

export const fmtUsd = (n: number, digits = 2) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: Math.max(digits, 2) });

export const fmtBrlFromUsd = (n: number, rate: number | null) =>
  rate ? (n * rate).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : null;
